import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { ArrowLeft, Blend, Columns2, CloudOff, Crosshair, Maximize2, Download, Layers, List, ListTree, Minus, Plus, RotateCw, Scan, WifiOff } from "lucide-react";
import { drw, errText, fetchSheetPdf, exportUrl } from "./api";
import { cacheSheet, cachedSheet } from "./offlineCache";
import { overlaySheet } from "./offlineMarkups";
import { isReplaying, onSync } from "../fieldops/offline/outbox";
import { uuid } from "../fieldops/offline/db";
import PdfStage, { sheetToCss } from "./PdfStage";
import MarkupOverlay from "./MarkupOverlay";
import Toolbar from "./Toolbar";
import LayerPanel from "./LayerPanel";
import MarkupList from "./MarkupList";
import MarkupDetail from "./MarkupDetail";
import PlanTicker from "./PlanTicker";
import PlanSave from "./PlanSave";
import UnlinkedMark from "./UnlinkedMark";
import { PURPOSE_STYLE } from "./FieldShopLinks";
import Legend from "./Legend";
import TakeoffPanel from "./TakeoffPanel";
import FinishWizard from "./FinishWizard";
import { paletteEntryFor, useTakeoff } from "./useTakeoff";
import ExpandedControls from "./ExpandedControls";
import SheetSwitcher from "./SheetSwitcher";
import { homeGroup, stepByType } from "./SheetSwitcher";

function TextPrompt({ title, placeholder, onOk, onCancel, extra }) {
  const [v, setV] = useState("");
  return (
    <div className="fixed inset-0 z-50 bg-black/30 grid place-items-center p-4" data-testid="text-prompt">
      <form className="bg-white rounded-xl p-4 w-full max-w-sm space-y-3 shadow-2xl" onSubmit={(e) => { e.preventDefault(); if (v.trim()) onOk(v.trim()); }}>
        <div className="font-semibold">{title}</div>
        <textarea autoFocus value={v} onChange={(e) => setV(e.target.value)} placeholder={placeholder} rows={3}
                  className="w-full border rounded-md p-2 text-sm" data-testid="text-prompt-input" />
        {extra}
        <div className="flex justify-end gap-2">
          <button type="button" onClick={onCancel} className="px-3 py-1.5 text-sm rounded-md border" data-testid="text-prompt-cancel">Cancel</button>
          <button type="submit" className="px-3 py-1.5 text-sm rounded-md bg-orange-500 text-white" data-testid="text-prompt-ok">Place</button>
        </div>
      </form>
    </div>
  );
}

/** PLAN-COMPLETE §1 — the Space outline is a boundary, a room label and its shading, nothing
 *  more. "Complete items…" and "All installed in this room" were earlier plan-side completion and
 *  are gone: a room-level MARK carries its own room now (§10). */
function SpaceCard({ sp, rs, onClose, onOpenFrs }) {
  const ref = sp.frs_room_ref || {};
  return (
    <div className="bg-white border rounded-xl shadow-xl p-3 w-72 space-y-2 text-sm" data-testid="space-card">
      <div className="flex items-start gap-2">
        <div className="flex-1">
          <div className="font-semibold">{ref.room_number ? `${ref.room_number} · ${ref.room_name || ""}` : sp.name}</div>
          <div className="text-[11px] text-neutral-500">{ref.room_number ? `Space "${sp.name}" → FRS room` : "Space matches no FRS room"}</div>
          {rs && <div className={`text-xs font-medium mt-1 ${rs.complete ? "text-green-700" : "text-amber-700"}`} data-testid="space-room-state">{rs.complete ? "All items installed" : `${rs.items_done} of ${rs.items} items complete · ${rs.installed} of ${rs.total} units`}</div>}
        </div>
        <button type="button" onClick={onClose} className="text-neutral-400 hover:text-neutral-700" data-testid="space-card-close">✕</button>
      </div>
      <div className="flex flex-wrap gap-1">
        {ref.room_number && <button type="button" onClick={() => onOpenFrs(ref.room_number, "")} className="text-xs px-3 min-h-11 rounded-md border" data-testid="space-open-frs">Open in FRS</button>}
      </div>
    </div>
  );
}

/** The viewer. One sheet, PDF.js underneath, every visual is data over it. */
export default function SheetViewer({ projectId, sheetId, onSwitchSheet, onBack, deepLink, onOpenFrs, me, canWrite, isField, linkScopes = [], onScopesChange,
                                      compareWith, onCompare, sheets = [], expanded = false, onExpand, activeGroup = "", onGroupPicked }) {
  const [detail, setDetail] = useState(null);
  // PLAN-COMPLETE §4 — the ticker's selection. Ids only, so it survives every pan, zoom and
  // redraw of the stage; the marks themselves are re-read from the server on every load.
  const [taps, setTaps] = useState(new Set());
  const [planSave, setPlanSave] = useState(null);     // { markupIds }
  const [unlinked, setUnlinked] = useState(null);
  const [pdf, setPdf] = useState(null);
  const [offline, setOffline] = useState(false);
  const [zoom, setZoom] = useState(1);
  const [rotation, setRotation] = useState(0);
  const [shape, setShape] = useState("cloud");      // FT-1 — one shape selector: square · cloud · circle
  const [tool, setTool] = useState("pan");
  const [color, setColor] = useState("#b91c1c");
  const [width, setWidth] = useState(2);
  const [stamps, setStamps] = useState(null);
  const [stamp, setStamp] = useState("");
  const [hidden, setHidden] = useState(null);
  const [hiddenAuthors, setHiddenAuthors] = useState(new Set());
  const [showSpaces, setShowSpaces] = useState(true);
  const [panel, setPanel] = useState(null);           // layers | list | null
  const [selected, setSelected] = useState(null);
  const [space, setSpace] = useState(null);
  const [draft, setDraft] = useState(null);
  const [prompt, setPrompt] = useState(null);
  const [moving, setMoving] = useState(null);
  const [resizing, setResizing] = useState(null);
  const [highlight, setHighlight] = useState(new Set());
  const [overlay, setOverlay] = useState(null);     // { revId, showSheet, opacity, showMarkups, showChanges }
  const [ghost, setGhost] = useState(null);         // the older revision: { pdf, detail }
  const [changeCount, setChangeCount] = useState(null);
  // DRW-8 — the legend, shown by default, hide/show remembered per user; a row highlights its symbol
  // shown by default on a desktop; on a phone the bottom sheet waits to be asked for (remembered either way)
  const [legendOpen, setLegendOpen] = useState(() => typeof window === "undefined" || window.innerWidth >= 900);
  // DRW-9 — expanded view (state lives in DrawingsTab so a sheet switch keeps it); the switcher;
  // the leave-with-marks confirm; and the canvas gesture guard for the control cluster
  const [switcher, setSwitcher] = useState(false);
  const [leaveAsk, setLeaveAsk] = useState(false);
  const gestureRef = useRef({ active: false, until: 0 });
  const gestureBusy = () => gestureRef.current.active || Date.now() < gestureRef.current.until;
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return undefined;
    const down = (e) => { if (e.target?.closest?.('[data-testid="expanded-controls"],[data-testid="expanded-tools-dropdown"],[data-testid="expanded-ticker"]')) return; gestureRef.current.active = true; };
    const up = () => { if (!gestureRef.current.active) return; gestureRef.current.active = false; gestureRef.current.until = Date.now() + 150; };
    // down is heard on the canvas; up / cancel on the window, because a pan can END over the cluster
    el.addEventListener("pointerdown", down, true); window.addEventListener("pointerup", up, true); window.addEventListener("pointercancel", up, true);
    el.addEventListener("touchstart", down, { capture: true, passive: true }); window.addEventListener("touchend", up, { capture: true, passive: true }); window.addEventListener("touchcancel", up, { capture: true, passive: true });
    return () => { el.removeEventListener("pointerdown", down, true); window.removeEventListener("pointerup", up, true); window.removeEventListener("pointercancel", up, true);
                   el.removeEventListener("touchstart", down, true); window.removeEventListener("touchend", up, true); window.removeEventListener("touchcancel", up, true); };
  }, [detail]);
  // entering / leaving keeps the pan: the scroll box is the same element, only its size changes
  const toggleExpanded = () => {
    const el = containerRef.current;
    const keep = el ? { l: el.scrollLeft, t: el.scrollTop } : null;
    onExpand?.(!expanded);
    requestAnimationFrame(() => requestAnimationFrame(() => { if (el && keep) { el.scrollLeft = keep.l; el.scrollTop = keep.t; } }));
  };
  useEffect(() => {
    document.body.classList.toggle("frs-field-expanded", expanded);   // same hook the Install view uses — hides Mira too
    if (!expanded) return undefined;
    const onKey = (e) => { if (e.key === "Escape") toggleExpanded(); };
    window.addEventListener("keydown", onKey);
    return () => { window.removeEventListener("keydown", onKey); document.body.classList.remove("frs-field-expanded"); };
    // eslint-disable-next-line
  }, [expanded]);
  // DRW-9a — Prev/Next move by sheet TYPE inside the group the user is working in (the last group
  // picked from in the switcher; by default the first scope of this sheet, else All sheets)
  const curGroup = activeGroup || homeGroup(sheets, sheetId);
  const prevSheet = stepByType(sheets, curGroup, sheetId, -1);
  const nextSheet = stepByType(sheets, curGroup, sheetId, 1);
  const [hiddenSyms, setHiddenSyms] = useState(() => new Set());   // DRW-A §17 — legend checkboxes
  // DRW-B §4 — the scopes still showing on the plan go back to the FRS as its scope filter
  const visibleScopes = () => {
    const all = new Map();
    (detail?.markups || []).forEach((m) => { if (m.symbol?.key && m.symbol?.scope_code) all.set(m.symbol.key, m.symbol.scope_code); });
    if (!hiddenSyms.size) return [];
    return [...new Set([...all].filter(([k]) => !hiddenSyms.has(k)).map(([, sc]) => sc))];
  };
  const backToFrs = () => { if (taps.size > 0) setLeaveAsk(true); else onOpenFrs?.("", "", visibleScopes()); };
  // the host's Back bar needs the same answer, so it is reported upward whenever it changes
  // eslint-disable-next-line
  useEffect(() => { onScopesChange?.(visibleScopes()); }, [hiddenSyms, detail?.sheet?.id]);
  useEffect(() => {
    if (!detail || !linkScopes.length) return;
    const want = new Set(linkScopes.map((x) => String(x).toUpperCase()));
    const hide = new Set();
    (detail.markups || []).forEach((m) => { const k = m.symbol?.key; if (k && !want.has(String(m.symbol?.scope_code || "").toUpperCase())) hide.add(k); });
    setHiddenSyms(hide);
    // eslint-disable-next-line
  }, [detail?.sheet?.id, linkScopes.join(",")]);
  const [legendTick, setLegendTick] = useState(0);
  const [marquee, setMarquee] = useState(null);
  // a mouse in the default pan tool never reaches the stage's pointer handlers, so while stamping
  // (or Shift is held for a copy / marquee) the stage is told a non-pan tool
  const [shiftHeld, setShiftHeld] = useState(false);
  useEffect(() => {
    const dn = (e) => { if (e.key === "Shift") setShiftHeld(true); };
    const up = (e) => { if (e.key === "Shift") setShiftHeld(false); };
    window.addEventListener("keydown", dn); window.addEventListener("keyup", up); window.addEventListener("blur", () => setShiftHeld(false));
    return () => { window.removeEventListener("keydown", dn); window.removeEventListener("keyup", up); };
  }, []);
  // the remembered open/closed state is honoured on a desktop; a phone's bottom sheet only opens when asked
  useEffect(() => { drw.get("/view-state", { params: { project_id: projectId, view: "legend" } }).then(({ data }) => { if (data?.state && typeof data.state.open === "boolean" && window.innerWidth >= 900) setLegendOpen(data.state.open); }).catch(() => {}); }, [projectId]);
  const toggleLegend = () => { const open = !legendOpen; setLegendOpen(open); if (open) setPanel((p) => (p === "layers" ? null : p)); drw.put("/view-state", { project_id: projectId, view: "legend", state: { open } }).catch(() => {}); };
  const containerRef = useRef(null);
  const vpRef = useRef(null);
  const frameRef = useRef(null);
  const fileRef = useRef(null);
  const pendingPhoto = useRef(null);

  // DRW-FIX-2 — on a phone the drawing zooms, the page does not. The viewport is locked only
  // while a sheet is open and restored on the way out, so the rest of the app still pinches.
  useEffect(() => {
    const meta = document.querySelector('meta[name="viewport"]');
    if (!meta) return undefined;
    const was = meta.getAttribute("content");
    meta.setAttribute("content", "width=device-width, initial-scale=1, maximum-scale=1, user-scalable=no, viewport-fit=cover");
    return () => meta.setAttribute("content", was);
  }, []);

  const load = useCallback(async (silent) => {
    try {
      const { data } = await drw.get(`/sheets/${sheetId}`);
      setDetail(data);
      setOffline(false);
      if (hidden === null) {
        const def = new Set(data.default_layers);
        setHidden(new Set(data.layers.filter((l) => !def.has(l))));
      }
      return data;
    } catch (e) {
      const c = await cachedSheet(sheetId);
      if (c) { const d = await overlaySheet(sheetId, c.detail); setDetail(d); setOffline(true); if (!pdf) setPdf(c.pdf); return d; }
      if (!silent) toast.error(errText(e, "Could not load the sheet"));
      return null;
    }
    // eslint-disable-next-line
  }, [sheetId]);

  // TKO-1/2 — takeoff mode
  const tk = useTakeoff({ projectId, sheetId, setId: detail?.sheet?.set_id, reload: async (q) => { const d = await load(q); setLegendTick((n) => n + 1); return d; } });
  useEffect(() => {
    if (!tk.session) return undefined;
    const onKey = (e) => {
      if (e.target && ["INPUT", "TEXTAREA", "SELECT"].includes(e.target.tagName)) return;
      if ((e.key === "Delete" || e.key === "Backspace") && tk.sel.size) { e.preventDefault(); tk.voidMarks([...tk.sel]); }
      else if (e.key === "Escape") { tk.setStamping(false); tk.setSel(new Set()); }
      else if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "z") { e.preventDefault(); e.shiftKey ? tk.redo() : tk.undo(); }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [tk]);

  // a replay just finished → the sheet re-reads so pending markups become the server's rows
  useEffect(() => {
    let was = isReplaying();
    return onSync(() => { const now = isReplaying(); if (was && !now) load(true); was = now; });
  }, [load]);
  // every write names its project (offline events, conflicts) and the layer the local copy is drawn on
  const wcfg = { _project_id: projectId, _layer: isField ? "Field" : "Ops" };
  const QUEUED = "Saved on this device — syncs when signal returns";

  // Overlay mode: the older revision's PDF + markups, fetched once per chosen revision (offline copy if cached)
  useEffect(() => {
    if (!overlay?.revId) { setGhost(null); setChangeCount(null); return undefined; }
    let alive = true;
    (async () => {
      try {
        const [{ data }, bytes] = await Promise.all([drw.get(`/sheets/${overlay.revId}`), fetchSheetPdf(overlay.revId)]);
        if (alive) setGhost({ pdf: bytes, detail: data });
      } catch {
        const c = await cachedSheet(overlay.revId);
        if (!alive) return;
        if (c) setGhost({ pdf: c.pdf, detail: c.detail }); else { toast.error("That revision is not on this device"); setOverlay(null); }
      }
    })();
    return () => { alive = false; };
  }, [overlay?.revId]);

  // FT-1 — ORIENTATION: a turn is remembered per sheet per device and re-applied on arrival, so
  // navigating away and back never loses it and never invents one (0 = as uploaded).
  // MARK-LINK-1 — the ticker and the "where did they go?" allocation window are GONE. A mark now
  // carries its own room and product (resolved at upload), so tapping one opens its detail card;
  // the install flow off the plan is rebuilt on the link in phase 2.

  const rotKey = `drw-rot-${sheetId}`;
  useEffect(() => {
    setRotation(Number(localStorage.getItem(rotKey) || 0) || 0);
    // eslint-disable-next-line
  }, [sheetId]);
  const turn = () => setRotation((r) => {
    const next = (r + 90) % 360;
    if (next) localStorage.setItem(rotKey, String(next)); else localStorage.removeItem(rotKey);
    return next;
  });

  useEffect(() => {
    setDetail(null); setPdf(null); setSelected(null); setSpace(null); setDraft(null); setOverlay(null);
    setTaps(new Set()); setPlanSave(null); setUnlinked(null);
    let alive = true;
    (async () => {
      const d = await load();
      if (!alive) return;
      try {
        const bytes = await fetchSheetPdf(sheetId);
        if (!alive) return;
        setPdf(bytes);
        if (d) cacheSheet(sheetId, bytes, d).catch(() => {});
      } catch {
        const c = await cachedSheet(sheetId);
        if (c && alive) { setPdf(c.pdf); setOffline(true); }
      }
    })();
    return () => { alive = false; };
  }, [sheetId, load]);

  useEffect(() => {
    drw.get(`/projects/${projectId}/stamps`).then(({ data }) => { setStamps(data); if (data.stamps?.[0]) setStamp(`stamp:${data.stamps[0].name}`); }).catch(() => {});
  }, [projectId]);

  // deep link: highlight markups, zoom to a room's Space
  useEffect(() => {
    if (!detail || !deepLink) return;
    if (deepLink.markupIds?.length) setHighlight(new Set(deepLink.markupIds));
    if (deepLink.markupId) {
      const m = detail.markups.find((x) => x.id === deepLink.markupId);
      if (m) setSelected(m);
    }
    // eslint-disable-next-line
  }, [detail?.sheet?.id, deepLink]);

  const zoomToSpace = useCallback((sp) => {
    const el = containerRef.current;
    const f = frameRef.current;
    if (!el || !f || !sp) return;
    const p = sp.geometry?.points || [];
    if (!p.length) return;
    const xs = p.map((q) => q[0]); const ys = p.map((q) => q[1]);
    const a = sheetToCss([Math.min(...xs), Math.min(...ys)], f);
    const b = sheetToCss([Math.max(...xs), Math.max(...ys)], f);
    const bw = Math.max(1, Math.abs(b[0] - a[0])); const bh = Math.max(1, Math.abs(b[1] - a[1]));
    const want = Math.min((el.clientWidth * 0.7) / bw, (el.clientHeight * 0.7) / bh);
    if (want > 1.15 || want < 0.85) { setZoom((z) => Math.max(0.25, Math.min(8, z * want))); return; }
    el.scrollTo({ left: Math.min(a[0], b[0]) - (el.clientWidth - bw) / 2, top: Math.min(a[1], b[1]) - (el.clientHeight - bh) / 2, behavior: "smooth" });
  }, []);
  const pendingZoom = useRef(null);
  useEffect(() => {
    if (!detail || !deepLink?.spaceId) return;
    pendingZoom.current = detail.spaces.find((s) => s.id === deepLink.spaceId) || null;
    // eslint-disable-next-line
  }, [detail?.sheet?.id, deepLink]);
  const onReady = (vp, frame) => { vpRef.current = vp; frameRef.current = frame; if (pendingZoom.current) { const sp = pendingZoom.current; setTimeout(() => zoomToSpace(sp), 30); pendingZoom.current = null; } };
  useEffect(() => { if (pendingZoom.current && vpRef.current) { const sp = pendingZoom.current; pendingZoom.current = null; zoomToSpace(sp); } }, [zoom, zoomToSpace]);

  const visible = useCallback((m) => !(hidden || new Set()).has(m.layer) && !hiddenAuthors.has(m.author)
    && !(m.symbol?.key && hiddenSyms.has(m.symbol.key)), [hidden, hiddenAuthors, hiddenSyms]);
  const counts = useMemo(() => (detail?.markups || []).reduce((acc, m) => { acc[m.layer] = (acc[m.layer] || 0) + 1; return acc; }, {}), [detail]);

  const place = async (body) => {
    try {
      const cid = uuid();   // same id on the header and the row → the server copy carries it, the local copy is deduped by it
      const { data, queued } = await drw.post(`/sheets/${sheetId}/markups`, { ...body, client_id: cid }, { ...wcfg, headers: { "X-Client-Write-Id": cid } });
      if (queued) toast.message(QUEUED);
      const d = await load(true);
      const id = queued ? data.client_id : data.id;
      const fresh = d?.markups?.find((x) => x.id === id);
      if (["stamp", "symbol", "sticky"].includes(body.type)) setSelected(fresh || (queued ? null : data));
      if (data.frs_review_flagged) toast.message("Symbol added — FRS flagged for review");
    } catch (e) { toast.error(errText(e)); }
  };

  const onPointer = (phase, pt, e) => {
    if (!canWrite && tool !== "pan") return;
    if (phase === "cancel") { setDraft(null); setMarquee(null); return; }
    if (moving?.takeoff && phase === "up") { tk.move(moving, pt); setMoving(null); setTool("pan"); return; }
    if (tk.session && tool === "pan" && !moving && !resizing) {
      // TKO-1 §3 — Shift+drag marquee selects many; Shift+click or Stamp mode places one copy per click
      if (e?.shiftKey && !tk.stamping) {
        if (phase === "down") { setMarquee({ a: pt, b: pt }); return; }
        if (phase === "move" && marquee) { setMarquee((mq) => (mq ? { ...mq, b: pt } : mq)); return; }
        if (phase === "up" && marquee && Math.hypot(marquee.b[0] - marquee.a[0], marquee.b[1] - marquee.a[1]) > 4) {
          const [x0, x1] = [Math.min(marquee.a[0], pt[0]), Math.max(marquee.a[0], pt[0])];
          const [y0, y1] = [Math.min(marquee.a[1], pt[1]), Math.max(marquee.a[1], pt[1])];
          const hit = detail.markups.filter((m) => (m.origin === "app" ? m.takeoff : (m.takeoff || m.mark_state)) && m.geometry?.rect && m.geometry.rect[0] >= x0 && m.geometry.rect[2] <= x1 && m.geometry.rect[1] >= y0 && m.geometry.rect[3] <= y1).map((m) => m.id);
          tk.setSel(new Set(hit)); setMarquee(null); return;
        }
        if (phase === "up") { setMarquee(null); if (tk.symbol) tk.stamp(pt); return; }
      }
      if (phase === "up" && tk.stamping && tk.symbol) { tk.stamp(pt); return; }
    }
    if (moving && phase === "up") {
      const g = moving.geometry?.x != null ? { ...moving.geometry, x: pt[0], y: pt[1] } : moving.geometry;
      drw.post(`/markups/${moving.id}/carry`, { action: "move", geometry: g }, wcfg).then(({ queued }) => { toast.success(queued ? QUEUED : "Moved"); setMoving(null); setTool("pan"); load(true); })
        .catch((err) => toast.error(errText(err)));
      return;
    }
    if (resizing && phase === "up") {
      const g = resizing.geometry || {};
      const r = g.rect || (g.points ? [Math.min(...g.points.map((p) => p[0])), Math.min(...g.points.map((p) => p[1])),
                                       Math.max(...g.points.map((p) => p[0])), Math.max(...g.points.map((p) => p[1]))] : null);
      if (!r) { setResizing(null); return; }
      const rect = [Math.min(r[0], pt[0]), Math.min(r[1], pt[1]), Math.max(r[0], pt[0]), Math.max(r[1], pt[1])];
      const geom = g.points
        ? { points: [[rect[0], rect[1]], [rect[2], rect[1]], [rect[2], rect[3]], [rect[0], rect[3]]] }
        : { rect };
      drw.patch(`/markups/${resizing.id}`, { geometry: geom }, wcfg)
        .then(({ queued }) => { toast.success(queued ? QUEUED : "Resized"); setResizing(null); setTool("pan"); load(true); })
        .catch((err) => toast.error(errText(err)));
      return;
    }
    const t = tool === "pan" && e?.pointerType === "pen" ? "pen" : tool;
    if (t === "pan") return;
    if (["pen", "highlighter"].includes(t)) {
      if (phase === "down") setDraft({ type: t, points: [pt], color, width });
      else if (phase === "move") setDraft((d) => (d ? { ...d, points: [...d.points, pt] } : d));
      else if (phase === "up") {
        const d = draft;
        setDraft(null);
        if (d && d.points.length > 1) place({ type: t, geometry: { strokes: [d.points] }, style: { stroke: color, width: t === "highlighter" ? 10 : width } });
      }
      return;
    }
    if (["shape", "arrow"].includes(t)) {
      const kind = t === "arrow" ? "arrow" : shape;             // square | cloud | circle
      if (phase === "down") setDraft({ type: t === "arrow" ? "arrow" : kind, points: [pt, pt], color });
      else if (phase === "move") setDraft((d) => (d ? { ...d, points: [d.points[0], pt] } : d));
      else if (phase === "up") {
        const d = draft; setDraft(null);
        if (!d) return;
        const [a, b] = [d.points[0], pt];
        if (Math.hypot(b[0] - a[0], b[1] - a[1]) < 3) return;
        const rect = [Math.min(a[0], b[0]), Math.min(a[1], b[1]), Math.max(a[0], b[0]), Math.max(a[1], b[1])];
        if (t === "arrow") place({ type: "arrow", geometry: { points: [a, b] }, style: { stroke: color, width: 2 } });
        else if (kind === "square") place({ type: "rectangle", geometry: { rect }, style: { stroke: color, width: 1.5 }, subject: "Square" });
        else if (kind === "circle") place({ type: "ellipse", geometry: { rect }, style: { stroke: color, width: 1.5 }, subject: "Circle" });
        else place({ type: "cloud", geometry: { points: [[rect[0], rect[1]], [rect[2], rect[1]], [rect[2], rect[3]], [rect[0], rect[3]]] }, style: { stroke: color, width: 1.5 }, subject: "Revision Cloud" });
      }
      return;
    }
    if (phase !== "up") return;
    if (t === "sticky") setPrompt({ title: "Sticky note", placeholder: "Note…", onOk: (text) => { setPrompt(null); place({ type: "sticky", geometry: { x: pt[0], y: pt[1] }, comments: text, subject: "Note" }); } });
    else if (t === "punch_pin") setPrompt({ title: "Punch item", placeholder: "What needs fixing?", onOk: (text) => { setPrompt(null); drw.post(`/sheets/${sheetId}/punch-pin`, { x: pt[0], y: pt[1], description: text }, wcfg).then(({ queued }) => { toast.success(queued ? QUEUED : "Punch item created"); load(true); }).catch((err) => toast.error(errText(err))); } });
    else if (t === "photo_pin") { pendingPhoto.current = pt; fileRef.current?.click(); }
    else if (t === "stamp") {
      const [kind, name] = (stamp || "stamp:Verify").split(":");
      place({ type: kind === "symbol" ? "symbol" : "stamp", geometry: { x: pt[0], y: pt[1], rect: [pt[0] - 8, pt[1] - 8, pt[0] + 8, pt[1] + 8] }, subject: name, stamp: name });
    }
  };

  const onPhoto = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    const pt = pendingPhoto.current;
    if (!f || !pt) return;
    const fd = new FormData();
    fd.append("x", pt[0]); fd.append("y", pt[1]); fd.append("file", f);
    try { const { queued } = await drw.post(`/sheets/${sheetId}/photo-pin`, fd, wcfg); toast.success(queued ? QUEUED : "Photo pinned"); load(true); } catch (err) { toast.error(errText(err)); }
  };

  /** PLAN-COMPLETE §4/§9/§10/§12 — what a tap on a mark does, decided by the mark's own link and
   *  the state of the FRS record behind it. A takeoff mark never opens the detail card. */
  const tapMark = (m, e) => {
    if (tk.session) {
      const isApp = m.origin === "app" && m.takeoff;
      if (e?.shiftKey && (m.symbol || isApp)) {          // a copy beside the original, one per click
        const c = m.geometry?.rect ? [(m.geometry.rect[0] + m.geometry.rect[2]) / 2 + 30, (m.geometry.rect[1] + m.geometry.rect[3]) / 2] : null;
        if (c) tk.stamp(c, paletteEntryFor(tk.palette, m), m.id);
        return;
      }
      if (isApp) { tk.setSel(new Set([m.id])); tk.setSymbol(paletteEntryFor(tk.palette, m)); setSelected(null); setUnlinked(null); return; }
      // P4-1 — an imported Bluebeam takeoff mark selects too, so Move works on it
      if (m.origin === "ingest" && (m.takeoff || m.mark_state)) { tk.setSel(new Set([m.id])); if (m.symbol) tk.setSymbol(paletteEntryFor(tk.palette, m)); setSelected(null); setUnlinked(null); return; }
      if (m.symbol) { tk.setSymbol(paletteEntryFor(tk.palette, m)); return; }
    }
    const ms = m.mark_state;
    if (!ms) { setSpace(null); setUnlinked(null); setSelected(m); return; }   // not a takeoff
    setSelected(null); setSpace(null);
    if (ms.state === "unlinked") { setUnlinked(m); return; }                  // §12
    if (!canWrite || !s.is_current) { setUnlinked(null); setSelected(m); return; }
    setUnlinked(null);
    // §9 a completed record opens for review, §10 a room-level mark opens its room — neither
    // touches the ticker
    if (ms.state === "installed" || ms.kind === "room") { setPlanSave({ markupIds: [m.id] }); return; }
    setTaps((cur) => { const n = new Set(cur); n.has(m.id) ? n.delete(m.id) : n.add(m.id); return n; });
  };

  if (!detail) return <div className="p-6 text-sm text-neutral-500" data-testid="viewer-loading">Loading sheet…</div>;
  const s = detail.sheet;
  const shopSet = s.kind === "field_shop" ? detail.set : null;     // DRW-6 — a shop sheet: view-only, purpose up front
  const shopStyle = shopSet ? (PURPOSE_STYLE[shopSet.purpose] || PURPOSE_STYLE.INSTALL) : null;
  const ShopIcon = shopStyle?.Icon;
  const verifyCount = detail.markups.filter((m) => m.verify_state === "verify").length;
  const pendingCount = detail.markups.filter((m) => m.pending || m.pending_edit).length;
  const conflictCount = detail.markups.filter((m) => m.conflict).length;
  const compact = typeof window !== "undefined" && window.innerWidth < 900;
  const prevRev = detail.revisions.find((r) => r.id !== s.id && r.revision_no === s.revision_no - 1) || detail.revisions.find((r) => r.id !== s.id);
  const carried = new Set(detail.markups.map((m) => m.carried_from).filter(Boolean));
  const sig = (m) => { const g = m.geometry || {}; const p = g.x != null ? [g.x, g.y] : g.rect ? [(g.rect[0] + g.rect[2]) / 2, (g.rect[1] + g.rect[3]) / 2] : ((g.points || (g.strokes || []).flat())[0] || [0, 0]); return `${m.type}|${m.subject || ""}|${Math.round(p[0] / 3)}|${Math.round(p[1] / 3)}`; };
  const present = new Set(detail.markups.map(sig));
  // only what did NOT make it onto this revision: not carried forward and not re-read from the new PDF at the same spot
  const ghostMarkups = ghost?.detail?.markups?.filter((m) => !carried.has(m.id) && !present.has(sig(m))) || [];
  // PLAN-COMPLETE — the ticker's figures. A product mark is one unit; a LENGTH takeoff is its FRS
  // record as a whole, so it contributes its full linked quantity, never one unit per foot (§11).
  const tapped = detail.markups.filter((m) => taps.has(m.id));
  const tapUnits = tapped.reduce((n, m) => n + Number(m.mark_state?.tap_units || 1), 0);
  const takeoffs = detail.markups.filter((m) => m.mark_state);
  // §12 / the set has no room numbers at all — say so plainly rather than looking broken
  const noRooms = takeoffs.length > 0
    && takeoffs.every((m) => m.mark_state.state === "unlinked" && m.mark_state.why === "no_room");
  // TKO — session marks read dashed until Finish; a legend row highlights every mark of its symbol
  const shownMarkups = detail.markups.map((m) => (tk.session && m.session_id === tk.session.id && m.status !== "voided" && tk.session.status === "open" ? { ...m, session_live: true } : m));
  const sheetCounts = detail.markups.reduce((acc, m) => { const k = m.symbol?.key; if (k && m.status !== "voided") acc[k] = (acc[k] || 0) + 1; return acc; }, {});
  const selectedFresh = selected ? detail.markups.find((m) => m.id === selected.id || (m.client_id && m.client_id === selected.id)) || (selected.pending ? null : selected) : null;

  return (
    <div className={expanded
      ? "fixed inset-0 z-50 flex flex-col min-w-0 bg-neutral-100 frs-field-shell overflow-hidden"
      : "flex flex-col h-[calc(100vh-11rem)] min-h-[520px] min-w-0 bg-neutral-100 rounded-xl border border-neutral-200 overflow-hidden"}
         data-testid="sheet-viewer" data-expanded={expanded ? "1" : undefined}>
      {shopSet && (
        <div className={`flex items-center gap-3 px-3 py-2 text-sm font-bold flex-wrap ${shopStyle.banner}`} data-testid="shop-sheet-banner" data-purpose={shopSet.purpose}>
          <ShopIcon className="w-5 h-5" />
          <span className="text-base tracking-wide">{shopStyle.label}</span>
          <span className="font-semibold opacity-90">{(shopSet.scopes || []).join(" / ")} · field shop drawing</span>
          <span className="font-normal text-xs opacity-80">rooms: {(s.room_refs || []).map((r) => r.room_number).join(", ") || "none assigned"}{shopSet.published ? "" : " · NOT PUBLISHED"}</span>
        </div>
      )}
      {!expanded && <div className="flex items-center gap-2 px-3 py-2 bg-white border-b text-sm flex-wrap">
        {onBack && <button type="button" onClick={onBack} className="h-11 min-w-11 px-2 grid place-items-center rounded-md border text-xs font-medium" aria-label="Back to the previous sheet" data-testid="sheet-back"><span className="flex items-center gap-1"><ArrowLeft className="w-4 h-4" /> Back</span></button>}
        <div className="font-mono font-bold text-base" data-testid="viewer-sheet-number">{s.sheet_number}</div>
        <div className="truncate max-w-[24rem]" data-testid="viewer-sheet-title">{s.title}</div>
        {!shopSet && <select value={s.id} onChange={(e) => onSwitchSheet(e.target.value)} className="h-7 text-xs border rounded-md px-1" data-testid="viewer-revision-switch">
          {detail.revisions.map((r) => <option key={r.id} value={r.id}>Rev {r.revision_no} · {r.revision_label}{r.is_current ? " (current)" : ""}</option>)}
        </select>}
        <span className="text-xs text-neutral-500" data-testid="viewer-revision-label">{s.revision_label}{s.issued_at ? ` · issued ${s.issued_at.slice(0, 10)}` : ""}</span>
        {!s.is_current && !shopSet && <span className="text-xs px-2 py-0.5 rounded bg-neutral-800 text-white" data-testid="viewer-prior-banner">Prior revision — read only</span>}
        {s.is_current && s.revision_no > 1 && <span className="text-xs px-2 py-0.5 rounded bg-orange-100 text-orange-800 font-medium" data-testid="viewer-asi-banner">ASI · Rev {s.revision_no}{s.has_revision_cloud ? " · clouds on sheet" : ""}{verifyCount ? ` · ${verifyCount} to verify` : ""}</span>}
        {s.not_reissued && <span className="text-xs px-2 py-0.5 rounded bg-amber-100 text-amber-800" data-testid="viewer-not-reissued">Not reissued in latest set</span>}
        {offline && <span className="text-xs px-2 py-0.5 rounded bg-neutral-200 flex items-center gap-1" data-testid="viewer-offline"><WifiOff className="w-3 h-3" /> offline copy</span>}
        {pendingCount > 0 && <span className={`text-xs px-2 py-0.5 rounded flex items-center gap-1 ${conflictCount ? "bg-red-100 text-red-800" : "bg-amber-100 text-amber-800"}`} data-testid="viewer-pending"><CloudOff className="w-3 h-3" /> {pendingCount} waiting to sync{conflictCount ? ` · ${conflictCount} to review` : ""}</span>}
        <div className="ml-auto flex items-center gap-1">
          <PlanTicker count={taps.size} units={tapUnits} busy={!!planSave}
                      onClear={() => setTaps(new Set())}
                      onSave={() => setPlanSave({ markupIds: [...taps] })} />
          <button type="button" onClick={() => setZoom((z) => Math.max(0.25, z / 1.25))} className="h-8 w-8 grid place-items-center rounded-md border" aria-label="Zoom out" data-testid="zoom-out"><Minus className="w-4 h-4" /></button>
          <span className="text-xs w-10 text-center" data-testid="zoom-level">{Math.round(zoom * 100)}%</span>
          <button type="button" onClick={() => setZoom((z) => Math.min(8, z * 1.25))} className="h-8 w-8 grid place-items-center rounded-md border" aria-label="Zoom in" data-testid="zoom-in"><Plus className="w-4 h-4" /></button>
          <button type="button" onClick={() => setZoom(1)} className="h-8 w-8 grid place-items-center rounded-md border" aria-label="Fit" data-testid="zoom-fit"><Scan className="w-4 h-4" /></button>
          <button type="button" onClick={turn} className="h-8 w-8 grid place-items-center rounded-md border" aria-label="Rotate" data-testid="rotate"><RotateCw className="w-4 h-4" /></button>
          {!shopSet && (
            <button type="button" onClick={toggleLegend} className={`h-8 px-2 rounded-md border flex items-center gap-1 text-xs ${legendOpen ? "bg-neutral-900 text-white" : ""}`} data-testid="toggle-legend"><ListTree className="w-4 h-4" /> Legend</button>
          )}
          {!shopSet && s.is_current && detail.takeoff_can_edit && canWrite && (
            <button type="button" onClick={() => (tk.session ? tk.exit() : tk.enter())} className={`h-8 px-2 rounded-md border flex items-center gap-1 text-xs font-semibold ${tk.session ? "bg-orange-500 text-white border-orange-500" : "border-orange-400 text-orange-800"}`} data-testid="toggle-takeoff"><Crosshair className="w-4 h-4" /> Takeoff</button>
          )}
          <button type="button" onClick={() => { const next = panel === "layers" ? null : "layers"; setPanel(next); if (next && legendOpen) { setLegendOpen(false); } }} className={`h-8 px-2 rounded-md border flex items-center gap-1 text-xs ${panel === "layers" ? "bg-neutral-900 text-white" : ""}`} data-testid="toggle-layers"><Layers className="w-4 h-4" /> Layers</button>
          <button type="button" onClick={() => setPanel(panel === "list" ? null : "list")} className={`h-8 px-2 rounded-md border flex items-center gap-1 text-xs ${panel === "list" ? "bg-neutral-900 text-white" : ""}`} data-testid="toggle-markup-list"><List className="w-4 h-4" /> Markups <span className="text-[10px] opacity-70">{detail.markups.length}</span></button>
          {detail.revisions.length > 1 && (
            <button type="button" onClick={() => { setOverlay(null); onCompare?.(compareWith ? null : prevRev?.id); }}
                    className={`h-8 px-2 rounded-md border flex items-center gap-1 text-xs ${compareWith ? "bg-neutral-900 text-white" : ""}`} data-testid="toggle-compare"><Columns2 className="w-4 h-4" /> Compare</button>
          )}
          {detail.revisions.length > 1 && (
            <button type="button" onClick={() => { if (overlay) setOverlay(null); else { onCompare?.(null); setOverlay({ revId: prevRev?.id, showSheet: true, opacity: 0.5, showMarkups: true, showChanges: true }); } }}
                    className={`h-8 px-2 rounded-md border flex items-center gap-1 text-xs ${overlay ? "bg-neutral-900 text-white" : ""}`} data-testid="toggle-overlay"><Blend className="w-4 h-4" /> Overlay</button>
          )}
          {compact && (
            <>
              <button type="button" onClick={() => setSwitcher(true)} className="h-8 px-2 rounded-md border flex items-center gap-1 text-xs" data-testid="open-sheets"><Layers className="w-4 h-4" /> Sheets</button>
              <button type="button" onClick={() => prevSheet && onSwitchSheet(prevSheet.id)} disabled={!prevSheet} className="h-8 w-8 grid place-items-center rounded-md border disabled:opacity-35" aria-label="Previous sheet in this group" data-testid="sheet-prev">‹</button>
              <button type="button" onClick={() => nextSheet && onSwitchSheet(nextSheet.id)} disabled={!nextSheet} className="h-8 w-8 grid place-items-center rounded-md border disabled:opacity-35" aria-label="Next sheet in this group" data-testid="sheet-next">›</button>
              {onExpand && <button type="button" onClick={toggleExpanded} className="h-8 w-8 grid place-items-center rounded-md border" aria-label="Fill the screen with the drawing" data-testid="expand-view"><Maximize2 className="w-4 h-4" /></button>}
            </>
          )}
          <a href={exportUrl("sheets", s.id, { with_markups: true })} target="_blank" rel="noreferrer" className="h-8 px-2 rounded-md border flex items-center gap-1 text-xs" data-testid="export-sheet"><Download className="w-4 h-4" /> Export</a>
        </div>
      </div>}

      {noRooms && !expanded && (
        <div className="px-3 py-1.5 bg-amber-50 border-b border-amber-200 text-[12px] text-amber-900 shrink-0" data-testid="viewer-no-room-numbers">
          No takeoff markup on this set carries a Room Number, so nothing here can be completed from the plan.
          Complete this work in the Install view, or add the room numbers in Bluebeam and upload the set again.
        </div>
      )}
      {overlay && !expanded && (
        <div className="flex items-center gap-3 px-3 py-1.5 bg-sky-50 border-b border-sky-200 text-xs flex-wrap max-w-full overflow-x-auto" data-testid="overlay-controls">
          <span className="font-semibold text-sky-900">Overlay</span>
          <select value={overlay.revId || ""} onChange={(e) => setOverlay({ ...overlay, revId: e.target.value })} className="h-7 border rounded-md px-1 bg-white" data-testid="overlay-rev">
            {detail.revisions.filter((r) => r.id !== s.id).map((r) => <option key={r.id} value={r.id}>Rev {r.revision_no} · {r.revision_label}</option>)}
          </select>
          <label className="flex items-center gap-1"><input type="checkbox" checked={overlay.showSheet} onChange={(e) => setOverlay({ ...overlay, showSheet: e.target.checked })} data-testid="overlay-old-sheet" /> Old sheet <span className="inline-block w-3 h-3 rounded-sm bg-sky-600/60" /></label>
          <label className="flex items-center gap-1">Opacity <input type="range" min={0.1} max={0.9} step={0.05} value={overlay.opacity} onChange={(e) => setOverlay({ ...overlay, opacity: Number(e.target.value) })} disabled={!overlay.showSheet} className="w-24" data-testid="overlay-opacity" /> <span className="w-8 tabular-nums" data-testid="overlay-opacity-value">{Math.round(overlay.opacity * 100)}%</span></label>
          <label className="flex items-center gap-1"><input type="checkbox" checked={overlay.showMarkups} onChange={(e) => setOverlay({ ...overlay, showMarkups: e.target.checked })} data-testid="overlay-old-markups" /> Old markups <span className="text-neutral-500">({ghostMarkups.length} gone or moved)</span></label>
          <label className="flex items-center gap-1"><input type="checkbox" checked={overlay.showChanges} onChange={(e) => setOverlay({ ...overlay, showChanges: e.target.checked })} data-testid="overlay-changes" /> Changed areas <span className="inline-block w-3 h-3 rounded-sm bg-orange-400/60 border border-orange-500" /></label>
          <span className="text-neutral-600" data-testid="overlay-change-count">{!ghost ? "loading older revision…" : changeCount === null ? "comparing…" : changeCount === 0 ? "no drawing change found (title block ignored)" : `${changeCount} changed area${changeCount === 1 ? "" : "s"}`}</span>
        </div>
      )}
      <div className="flex flex-1 min-h-0 min-w-0 relative">
        {!compact && (
          <div className="p-2 shrink-0">
            <Toolbar tool={tool} setTool={setTool} color={color} setColor={setColor} width={width} setWidth={setWidth}
                     shape={shape} setShape={setShape}
                     stamps={stamps} stamp={stamp} setStamp={setStamp} canWrite={canWrite && s.is_current} />
          </div>
        )}
        <div ref={containerRef} className="flex-1 min-w-0 min-h-0 overflow-auto overscroll-contain relative p-1" data-testid="viewer-scroll"
             onClick={() => { if (tool === "pan") { setSelected(null); setSpace(null); } }}>
          {pdf ? (
            <PdfStage pdfBytes={pdf} height={s.height} zoom={zoom} rotation={rotation} onZoom={setZoom} tool={moving || resizing ? "sticky" : tk.session && (tk.stamping || shiftHeld) && tool === "pan" ? "takeoff" : tool}
                      onPointer={onPointer} containerRef={containerRef} onReady={onReady}
                      ghost={overlay && ghost ? { pdfBytes: ghost.pdf, visible: overlay.showSheet, opacity: overlay.opacity, showChanges: overlay.showChanges, onChanges: setChangeCount } : null}>
              {({ scale, spin }) => (
                <>
                  {overlay?.showMarkups && ghostMarkups.length > 0 && (
                    <g opacity={0.45} style={{ filter: "grayscale(1)", pointerEvents: "none" }} data-testid="ghost-markups" data-count={ghostMarkups.length}>
                      <MarkupOverlay markups={ghostMarkups} spaces={[]} visible={() => true} selectedId={null} scale={scale} spin={spin} showSpaces={false} />
                    </g>
                  )}
                  <MarkupOverlay markups={shownMarkups} spaces={detail.spaces} visible={visible} selectedId={selectedFresh?.id || unlinked?.id || [...tk.sel][0]} scale={scale} spin={spin}
                                 onSelect={(m, e) => tapMark(m, e)}
                                 onSpaceTap={(sp) => { setSelected(null); setUnlinked(null); setSpace(sp); }}
                                 highlightIds={highlight} tappedIds={tk.session ? tk.sel : taps} draft={draft} showSpaces={showSpaces}
                                 roomStates={detail.room_states || {}} refs={detail.refs || []}
                                 onRef={(r) => onSwitchSheet(r.sheet_id, { push: true })} />
                  {marquee && <rect x={Math.min(marquee.a[0], marquee.b[0])} y={Math.min(marquee.a[1], marquee.b[1])} width={Math.abs(marquee.b[0] - marquee.a[0])} height={Math.abs(marquee.b[1] - marquee.a[1])} fill="rgba(249,115,22,0.12)" stroke="#f97316" strokeDasharray="4 3" vectorEffect="non-scaling-stroke" pointerEvents="none" data-testid="marquee" />}
                </>
              )}
            </PdfStage>
          ) : <div className="p-6 text-sm text-neutral-500">Rendering…</div>}
          {moving && <div className="absolute top-2 left-1/2 -translate-x-1/2 bg-orange-500 text-white text-xs px-3 py-1 rounded-full shadow" data-testid="moving-hint">Tap the new spot for this markup</div>}
          {resizing && <div className="absolute top-2 left-1/2 -translate-x-1/2 bg-orange-500 text-white text-xs px-3 py-1 rounded-full shadow" data-testid="resizing-hint">Tap the opposite corner to resize it</div>}
        </div>
        {selectedFresh && (
          <div className="absolute right-2 top-2 z-20">
            <MarkupDetail m={selectedFresh} projectId={projectId} onClose={() => setSelected(null)} onChanged={() => load(true)}
                          onOpenFrs={onOpenFrs} canWrite={canWrite && s.is_current} me={me}
                          onMoveRequest={(m) => { setMoving(m); setSelected(null); toast.message("Tap where it belongs on this revision"); }}
                          onResizeRequest={(m) => { setResizing(m); setSelected(null); toast.message("Tap the opposite corner"); }} />
          </div>
        )}
        {space && !selectedFresh && (
          <div className="absolute right-2 top-2 z-20">
            <SpaceCard sp={space} rs={(detail.room_states || {})[space.id]} onClose={() => setSpace(null)} onOpenFrs={onOpenFrs} />
          </div>
        )}
        {unlinked && (
          // on a phone the viewer is narrower than this card, so it sits at the bottom of the
          // screen rather than being clipped in the corner
          <div className="fixed left-2 right-2 bottom-2 z-30 sm:absolute sm:left-auto sm:right-2 sm:top-2 sm:bottom-auto">
            <UnlinkedMark mark={unlinked} setId={s.set_id} projectId={projectId}
                          onClose={() => setUnlinked(null)} onChanged={() => load(true)} />
          </div>
        )}
        {expanded && (
          <>
            <ExpandedControls gestureBusy={gestureBusy} legendOpen={legendOpen}
                              tools={canWrite && s.is_current && !shopSet ? (
                                <Toolbar compact tool={tool} setTool={setTool} color={color} setColor={setColor} width={width} setWidth={setWidth}
                                         shape={shape} setShape={setShape} stamps={stamps} stamp={stamp} setStamp={setStamp} canWrite />) : null}
                              onLegend={toggleLegend} onSheets={() => setSwitcher(true)}
                              onPrev={() => prevSheet && onSwitchSheet(prevSheet.id)} onNext={() => nextSheet && onSwitchSheet(nextSheet.id)}
                              hasPrev={!!prevSheet} hasNext={!!nextSheet}
                              onBackToFrs={onOpenFrs ? backToFrs : null} onExit={toggleExpanded} />
            {taps.size > 0 && (
              <div className="absolute left-2 top-2 z-40" data-testid="expanded-ticker">
                <PlanTicker count={taps.size} units={tapUnits} busy={!!planSave} onClear={() => setTaps(new Set())} onSave={() => setPlanSave({ markupIds: [...taps] })} />
              </div>
            )}
          </>
        )}
        {tk.session && (
          <TakeoffPanel palette={tk.palette} symbol={tk.symbol} setSymbol={(x) => { tk.setSymbol(x); }} stamping={tk.stamping} setStamping={tk.setStamping}
                        session={tk.session} sheetCounts={sheetCounts} projectNet={tk.projectNet} undo={tk.undo} redo={tk.redo} canUndo={tk.canUndo} canRedo={tk.canRedo}
                        onFinish={() => tk.setFinish(true)} onDiscard={tk.discard} onExit={tk.exit} selectedCount={tk.sel.size} onVoidSelected={() => tk.voidMarks([...tk.sel])}
                        allScopes={tk.allScopes} setAllScopes={tk.setAllScopes} compact={compact}
                        canEditStyle={canWrite && detail.takeoff_can_edit}
                        onPatchSymbol={async (sym, body) => {
                          try {
                            let id = sym.id;
                            if (!id) { const { data } = await drw.post(`/projects/${projectId}/symbols/ensure`, { subject: sym.subject, label: sym.label_full || sym.label, scope_code: sym.scope_code || "", shape: sym.shape, color: sym.color, fill: !!sym.fill }); id = data.id; }
                            await drw.patch(`/symbols/${id}`, body); await load(true); setLegendTick((n) => n + 1); tk.refreshPalette?.();
                          } catch (e) { toast.error(errText(e)); }
                        }} />
        )}
        {tk.session && tk.sel.size > 0 && (
          <div className="absolute top-2 left-1/2 -translate-x-1/2 z-20 bg-neutral-900 text-white text-xs rounded-full px-3 py-1.5 flex items-center gap-2 shadow" data-testid="takeoff-selection-bar">
            {tk.sel.size} selected
            {tk.sel.size === 1 && <button type="button" onClick={() => { const m = detail.markups.find((x) => x.id === [...tk.sel][0]); if (m) { setMoving({ ...m, takeoff: true }); toast.message("Tap where it belongs"); } }} className="underline" data-testid="takeoff-move">Move</button>}
            <button type="button" onClick={() => tk.voidMarks([...tk.sel])} className="underline text-red-300" data-testid="takeoff-void">Void</button>
            <button type="button" onClick={() => tk.setSel(new Set())} className="opacity-70">clear</button>
          </div>
        )}
        {legendOpen && !shopSet && !tk.session && (
          <Legend sheetId={s.id} sheetNumber={s.sheet_number} projectId={projectId} refreshKey={`${legendTick}-${detail.markups.length}`} canEdit={canWrite && detail.takeoff_can_edit}
                  hiddenKeys={hiddenSyms} onToggleKey={(k) => setHiddenSyms((h) => { const n = new Set(h); n.has(k) ? n.delete(k) : n.add(k); return n; })}
                  onSetHidden={(n) => setHiddenSyms(new Set(n))} onRestyle={() => load(true)} onClose={toggleLegend} compact={compact} />
        )}
        {panel === "layers" && hidden && !tk.session && (
          <div className="absolute right-0 top-0 bottom-0 z-30 w-60 bg-white border-l shadow-xl p-3 overflow-auto" data-testid="layers-side">
            <LayerPanel layers={detail.layers} authors={detail.authors} hidden={hidden}
                        toggle={(l) => setHidden((h) => { const n = new Set(h); n.has(l) ? n.delete(l) : n.add(l); return n; })}
                        onAll={() => setHidden(new Set())} onNone={() => setHidden(new Set(detail.layers))}
                        hiddenAuthors={hiddenAuthors} toggleAuthor={(a) => setHiddenAuthors((h) => { const n = new Set(h); n.has(a) ? n.delete(a) : n.add(a); return n; })}
                        showSpaces={showSpaces} setShowSpaces={setShowSpaces} counts={counts} />
          </div>
        )}
      </div>
      {panel === "list" && (
        <div className="h-64 border-t bg-white shrink-0">
          <MarkupList projectId={projectId} markups={detail.markups} sheetScope={s.id} onChanged={() => load(true)}
                      onJump={(m) => { setPanel(null); setSelected(m); const sp = detail.spaces.find((x) => x.id === m.space_id); if (sp) zoomToSpace(sp); }} />
        </div>
      )}
      {compact && !expanded && (
        <div className="p-1.5 bg-neutral-100 border-t">
          <Toolbar compact tool={tool} setTool={setTool} color={color} setColor={setColor} width={width} setWidth={setWidth}
                   shape={shape} setShape={setShape}
                   stamps={stamps} stamp={stamp} setStamp={setStamp} canWrite={canWrite && s.is_current} />
        </div>
      )}
      {switcher && (
        <SheetSwitcher sheets={sheets} currentId={s.id} refs={detail.refs || []} unresolvedRefs={detail.unresolved_refs || []} compact={compact}
                       openScopes={[]} activeGroup={curGroup} onGroupPicked={onGroupPicked}
                       onPick={(id) => onSwitchSheet(id)} onClose={() => setSwitcher(false)} />
      )}
      {leaveAsk && (
        <div className="fixed inset-0 z-[70] bg-black/40 grid place-items-center p-4" data-testid="leave-confirm">
          <div className="bg-white rounded-xl p-4 w-full max-w-sm space-y-3">
            <div className="text-sm font-semibold">Leave with {taps.size} mark{taps.size === 1 ? "" : "s"} selected? They will be cleared.</div>
            <div className="flex justify-end gap-2">
              <button type="button" autoFocus onClick={() => setLeaveAsk(false)} className="h-11 px-4 rounded-md bg-neutral-900 text-white text-sm font-semibold" data-testid="leave-stay">Stay</button>
              <button type="button" onClick={() => { setLeaveAsk(false); setTaps(new Set()); onOpenFrs?.("", "", visibleScopes()); }} className="h-11 px-4 rounded-md border text-sm" data-testid="leave-go">Leave</button>
            </div>
          </div>
        </div>
      )}
      {tk.finish && tk.session && (
        <FinishWizard projectId={projectId} session={tk.session} scopes={tk.palette?.scopes || []}
                      onClose={() => { tk.setFinish(false); tk.exit(); load(true); setLegendTick((n) => n + 1); }}
                      onFinished={() => load(true)} />
      )}
      {planSave && (
        <PlanSave sheetId={s.id} sheetNumber={s.sheet_number} projectId={projectId} author={me || ""}
                  markupIds={planSave.markupIds}
                  onClose={() => setPlanSave(null)}
                  onRoomSaved={(ids) => setTaps((cur) => { const n = new Set(cur); ids.forEach((i) => n.delete(i)); return n; })}
                  onAllDone={() => load(true)} />
      )}
      <input ref={fileRef} type="file" accept="image/*" capture="environment" className="hidden" onChange={onPhoto} data-testid="photo-pin-input" />
      {prompt && <TextPrompt {...prompt} onCancel={() => setPrompt(null)} />}
    </div>
  );
}
